/*
 * <collection-carousel> — arrow navigation + scroll progress bar for the
 * Collection Grid section when it is rendered in carousel mode.
 *
 * Markup (track, cards, arrows, scroll track) is rendered by Liquid. This
 * component scrolls the native scroll-snap track, reflects scroll position on
 * the prev/next buttons (`disabled` attribute) and slides the scroll bar
 * thumb — mirroring the section-mps__scroll-track pattern. In grid mode the
 * track has no overflow, so scrolling is a no-op and the arrows / scroll track
 * are hidden by CSS.
 */
const SCROLL_THUMB_WIDTH = 15;

class CollectionCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.scrollBar = this.querySelector('[data-scroll-bar]');
    if (!this.track) return;

    this.handleClick = this.handleClick.bind(this);
    this.update = this.update.bind(this);

    this.addEventListener('click', this.handleClick);
    this.track.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update);

    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.track?.removeEventListener('scroll', this.update);
    window.removeEventListener('resize', this.update);
  }

  handleClick(event) {
    const isNext = event.target.closest('[data-carousel-next]');
    const isPrev = event.target.closest('[data-carousel-prev]');
    if (!isNext && !isPrev) return;

    const direction = isNext ? 1 : -1;
    const amount = Math.max(this.track.clientWidth * 0.9, 200);
    this.track.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }

  update() {
    this.updateArrows();
    this.updateScrollBar();
  }

  updateArrows() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const maxScroll = scrollWidth - clientWidth - 1;

    this.prevButton?.toggleAttribute('disabled', scrollLeft <= 0);
    this.nextButton?.toggleAttribute('disabled', scrollLeft >= maxScroll);
  }

  updateScrollBar() {
    if (!this.scrollBar) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (maxScroll <= 0) return;

    const progress = this.track.scrollLeft / maxScroll;
    const trackWidth = this.scrollBar.parentElement.clientWidth;
    this.scrollBar.style.transform = `translateX(${progress * (trackWidth - SCROLL_THUMB_WIDTH)}px)`;
  }
}

if (!customElements.get('collection-carousel')) {
  customElements.define('collection-carousel', CollectionCarousel);
}
